// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anuj <anuj@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

export const DATE_DASH = "—";

export function rawTimeIsMidnight(value: string): boolean {
  const match = value.match(/T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?/);
  if (!match) return true;
  return (
    match[1] === "00" &&
    match[2] === "00" &&
    (match[3] ?? "00") === "00" &&
    Number((match[4] ?? "0").padEnd(3, "0")) === 0
  );
}

export function formatDisplayDateTime(
  value?: string | null,
  options: { timeZone?: string; fallback?: string; time?: boolean } = {},
): string {
  const raw = (value ?? "").trim();
  const fallback = options.fallback ?? DATE_DASH;
  if (!raw) return fallback;

  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const d = new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    );
    if (Number.isNaN(d.getTime())) return raw;
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(d);
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;

  return new Intl.DateTimeFormat(undefined, {
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(options.time === false || rawTimeIsMidnight(raw)
      ? {}
      : { hour: "numeric", minute: "2-digit" }),
  }).format(d);
}

export function formatLongDisplayDate(value: Date = new Date()): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(value);
}

export function formatDobDisplay(value?: string | null): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : raw;
}
